import { useState, useCallback } from 'react';
import { Homeless } from 'types';
import { 
    fetchPinResetLink, 
    fetchAuthLink, 
    sendPinResetLink, 
    sendAuthLink 
} from 'api/homeless';

//State Types
interface WebPortalLinkState {
    isLoading: boolean;
    error: string | null;
    link: string | null;  
}

interface WebPortalLinksState {
    pinReset: WebPortalLinkState;
    authLink: WebPortalLinkState;
}

//Hook
const useAdminMemberWebPortalLinks = (homeless: Homeless) => {
    //State
    const [state, setState] = useState<WebPortalLinksState>({
        pinReset: {
            isLoading: false,
            error: null,
            link: null
        },
        authLink: {
            isLoading: false,
            error: null,  
            link: null
        }
    });

    //Generate Pin Reset Link
    const generatePinResetLink = useCallback(async () => {
        //Set Loading State
        setState(prev => ({
            ...prev,
            pinReset: {
                ...prev.pinReset,
                isLoading: true,
                error: null,
                canSendEmail: (homeless.email || homeless.phone) ? true : false
            }
        }));

        //Fetch Pin Reset Link
        try {
            const response = await fetchPinResetLink(homeless.id);

            //Check for Error
            if (!response || !response.ok) {
                const errorData = response ? await response.json() : {};
                throw new Error(errorData?.message || 'Failed to generate PIN reset link');
            }

            //Get Link
            const data = await response.json();
            const link = data?.link || null;

            //Set Link
            setState(prev => ({
                ...prev,
                pinReset: {
                    isLoading: false,
                    error: null,
                    link
                }
            }));

            //Return Success
            return true;

        } catch (error) {
            //Set Error
            setState(prev => ({
                ...prev,
                pinReset: {
                    isLoading: false,
                    error: error instanceof Error ? error.message : 'An unknown error occurred',
                    link: null
                }
            }));

            //Return Failure
            return false;
        }
    }, [homeless.id, homeless.email, homeless.phone]);

    //Generate Auth Link
    const generateAuthLink = useCallback(async () => {
        //Set Loading State
        setState(prev => ({
            ...prev,
            authLink: {
                ...prev.authLink,
                isLoading: true,
                error: null
            }
        }));

        //Fetch Auth Link
        try {
            const response = await fetchAuthLink(homeless.id);

            //Check for Error
            if (!response || !response.ok) {
                const errorData = response ? await response.json() : {};
                throw new Error(errorData?.message || 'Failed to generate auth link');
            }

            //Get Link
            const data = await response.json();
            const link = data?.auth_link || data?.link || null;

            //Set Link
            setState(prev => ({
                ...prev,
                authLink: {
                    isLoading: false,
                    error: null,
                    link
                }
            }));

            //Return Success
            return true;

        } catch (error) {
            //Set Error
            setState(prev => ({
                ...prev,
                authLink: {
                    isLoading: false,
                    error: error instanceof Error ? error.message : 'An unknown error occurred',
                    link: null
                }
            }));

            //Return Failure
            return false;
        }
    }, [homeless.id]);

    //Send Pin Reset Email
    const sendPinResetEmail = useCallback(async () => {
        try {
            const response = await sendPinResetLink(homeless.id);

            //Check for Error
            if (!response || !response.ok) {
                const errorData = response ? await response.json() : {};
                throw new Error(errorData?.message || 'Failed to send PIN reset link');
            }

            //Return Success
            return { success: true };

        } catch (error) {
            //Set Error
            return { 
                success: false, 
                error: error instanceof Error ? error.message : 'An unknown error occurred' 
            };
        }
    }, [homeless.id]);

    //Send Auth Link Email
    const sendAuthLinkEmail = useCallback(async () => {
        try {
            const response = await sendAuthLink(homeless.id);

            //Check for Error
            if (!response || !response.ok) {
                const errorData = response ? await response.json() : {};
                throw new Error(errorData?.message || 'Failed to send auth link');
            }

            //Return Success
            return { success: true };

        } catch (error) {
            //Set Error
            return { 
                success: false, 
                error: error instanceof Error ? error.message : 'An unknown error occurred' 
            };
        }
    }, [homeless.id]);

    //Reset Pin Reset State
    const resetPinReset = useCallback(() => {
        setState(prev => ({
            ...prev,
            pinReset: {
                isLoading: false,
                error: null,
                link: null
            }
        }));
    }, []);

    //Reset Auth Link State
    const resetAuthLink = useCallback(() => {
        setState(prev => ({
            ...prev,
            authLink: {
                isLoading: false,
                error: null,
                link: null
            }
        }));
    }, []);

    //Return State
    return {
        // PIN Reset
        pinReset: {
            generate: generatePinResetLink,
            sendEmail: sendPinResetEmail,
            reset: resetPinReset,
            ...state.pinReset
        },
        // Auth Link  
        authLink: {
            generate: generateAuthLink,
            sendEmail: sendAuthLinkEmail,
            reset: resetAuthLink,
            ...state.authLink
        }
    };
};

export default useAdminMemberWebPortalLinks;